import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconComponent from "@CubeComponents/icons";
import IconButton from "@mui/material/IconButton";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { Popper } from "@mui/material";
import type { AutocompleteRenderInputParams } from "@mui/material";
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import Text from "@CubeComponents/html/Text/Text";
import AddIcon from "@mui/icons-material/Add";
import ToolTipComponent from "@CubeComponents/html/tooltip";

// ---- Interfaces ----
interface Condition {
  id?: string;
  column: string;
  direction: "asc" | "desc";
}

interface ColumnDefinition {
  id?: string;
  field: string;
  /** Column header display name - must be wrapped in t() for i18n */
  name?: TranslatedString;
  disableGrouping?: boolean;
  disableSorting?: boolean;
  [key: string]: unknown;
}

interface ConditionRowProps {
  readonly item: Condition;
  readonly isFirst: boolean;
  readonly title: string;
  readonly columnDefinitions: ColumnDefinition[];
  readonly usedColumns: Set<string>;
  readonly onChangeColumn: (value: string) => void;
  readonly onToggleDirection: () => void;
  readonly onDelete: () => void;
}

export interface SortGroupModalProps {
  readonly isOpen: boolean;
  readonly anchorEl: HTMLElement | null;
  readonly onClose: () => void;
  readonly title: string;
  readonly columnDefinitions?: ColumnDefinition[];
  readonly conditions: Condition[];
  readonly setConditions: (conditions: Condition[] | ((prev: Condition[]) => Condition[])) => void;
  readonly width?: number | string;
  readonly toggleGrouping?: (action: "expand" | "collapse") => void;
}

interface DndSource {
  data?: Record<string, unknown>;
}

interface DndLocation {
  current?: {
    dropTargets?: Array<{ data?: Record<string, unknown> }>;
  };
}

// ---- Utils ----
/** Custom Popper so AutoComplete dropdowns render above the SortGroupModal (zIndex 1500) */
const ModalAutoCompletePopper = (props: React.ComponentProps<typeof Popper>) => (
  <Popper {...props} style={{ ...(props.style as React.CSSProperties), zIndex: 1501 }} />
);

function uid(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
export const ensureIds = (arr: Condition[]): Condition[] => {
  return (arr || []).map((c) => (c?.id ? c : { ...c, id: uid() }));
};

function ConditionRow({
  item,
  isFirst,
  title,
  columnDefinitions,
  usedColumns,
  onChangeColumn,
  onToggleDirection,
  onDelete,
}: ConditionRowProps) {
  const { t } = useTranslation();
  const rowRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const rowEl = rowRef.current;
    const handleEl = handleRef.current;

    if (!rowEl || !handleEl) return;

    const cleanupDrag = draggable({
      element: rowEl,
      dragHandle: handleEl,
      getInitialData: () => ({ id: item.id ?? "" }),
    });

    const cleanupDrop = dropTargetForElements({
      element: rowEl,
      getData: () => ({ id: item.id ?? "" }),
    });

    return () => {
      cleanupDrag();
      cleanupDrop();
    };
  }, [item.id]);
  let headerText;
  if (isFirst) {
    headerText = title === "Sort" ? t("Sort By") : t("Group By");
  } else {
    headerText = t("Then");
  }

  return (
    <div
      ref={rowRef}
      className="flex items-center gap-2 px-2 py-2 mb-2 border border-transparent hover:border-gray-200 rounded-md"
    >
      <span ref={handleRef} className="cursor-grab" style={{ pointerEvents: "auto" }}>
        <div style={{ pointerEvents: "none" }}>
          <IconComponent name="Drag" width="1rem" height="1rem" />
        </div>
      </span>

      <span className="w-20 text-sm font-medium">{headerText}</span>

      <AutoComplete
        disableClearable
        PopperComponent={ModalAutoCompletePopper}
        options={columnDefinitions.filter((col) => {
          if (["_checkbox_selector", "_move"].includes(col.id ?? "")) {
            return false;
          }
          if (usedColumns.has(col.field)) return false;
          if (title === "Group") return !col.disableGrouping;
          else return !col.disableSorting;
        })}
        value={columnDefinitions.find((col) => col.field === item.column) ?? undefined}
        onChange={(_e: React.SyntheticEvent, val: ColumnDefinition | null) => {
          if (val?.field) {
            onChangeColumn(val.field);
          }
        }}
        getOptionLabel={(v: ColumnDefinition) => (v?.name as string) || ""}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextFieldComponent
            {...params}
            size="small"
            placeholder={t("Select column")}
            className="px-2 py-1 flex-1 min-w-0"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {item.column && (
                    <IconButton size="small" onClick={onToggleDirection}>
                      <IconComponent
                        name={item.direction === "asc" ? "Sort Up" : "Sort Down"}
                        width="1.25rem"
                        height="1.25rem"
                      />
                    </IconButton>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ flex: 1, minWidth: "11.25rem" }}
      />

      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={onDelete} iconButton={true}>
        <IconComponent name="delete" width="1.25rem" height="1.25rem" />
      </ButtonComponent>
    </div>
  );
}

export default function SortGroupModal({
  isOpen,
  anchorEl,
  onClose,
  title,
  columnDefinitions = [],
  conditions,
  setConditions,
  toggleGrouping,
  width = "30rem",
}: SortGroupModalProps) {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLElement | null>(anchorEl);

  useEffect(() => {
    if (anchorEl) anchorRef.current = anchorEl;
  }, [anchorEl]);

  const [tempCondition, setTempCondition] = useState<Condition | null>(null);

  const isAllConditionsValid = (): boolean => (conditions || []).every((c) => c?.column);

  const handleAdd = () => {
    if (!isAllConditionsValid()) return;
    setTempCondition({ id: uid(), column: "", direction: "asc" });
  };

  const handleTempChange = (key: keyof Condition, value: string) => {
    const updated = { ...tempCondition!, [key]: value };
    setTempCondition(updated);
    if (updated.column) {
      setConditions([...(conditions || []), updated]);
      setTempCondition(null);
    }
  };

  const handleDelete = (idx: number) => {
    const updated = [...(conditions || [])];
    updated.splice(idx, 1);
    setConditions(updated);
  };

  const toggleDirection = (idx: number) => {
    const updated: Condition[] = [...(conditions || [])];
    const current = updated[idx];
    if (!current) return;
    updated[idx] = {
      ...current,
      direction: current.direction === "asc" ? "desc" : "asc",
    };
    setConditions(updated);
  };

  const handleDrop = (
    source: DndSource,
    location: DndLocation,
    setConditionsFn: React.Dispatch<React.SetStateAction<Condition[]>>,
  ) => {
    const fromId = source?.data?.id as string | undefined;
    const toId = location.current?.dropTargets?.[0]?.data?.id as string | undefined;

    if (!fromId || !toId || fromId === toId) return;

    setConditionsFn((prev: Condition[]) => {
      const arr = ensureIds(prev || []);
      const fromIdx = arr.findIndex((x) => x.id === fromId);
      const toIdx = arr.findIndex((x) => x.id === toId);

      if (fromIdx < 0 || toIdx < 0) return prev;

      const next = [...arr];
      const [moved] = next.splice(fromIdx, 1);
      if (!moved) return prev;
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    const stop = monitorForElements({
      onDrop({ source, location }) {
        handleDrop(source, location, setConditions as React.Dispatch<React.SetStateAction<Condition[]>>);
      },
    });

    return () => stop();
  }, [isOpen, setConditions]);

  if (!isOpen) return null;

  const list = ensureIds(conditions || []);
  const allUsedColumns = new Set(list.map((c) => c.column).filter(Boolean));

  let headerText;
  if (list.length === 0) {
    headerText = title === "Sort" ? t("Sort By") : t("Group By");
  } else {
    headerText = t("Then");
  }

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorRef.current}
      placement="bottom-start"
      style={{ zIndex: 1500 }}
      sx={{ mt: "2.5rem" }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          sx={{
            width,
            maxHeight: "15.625rem",
            borderRadius: "0.5rem",
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            padding: "1.5rem !important",
          }}
        >
          {/* Header */}
          <div className="flex justify-between items-center border-b border-[#C6D8EC]  pb-4 shrink-0">
            <Text className="text-2xl font-semibold">{title}</Text>
            <div className="gap-2 flex">
              {title === "Group" && (
                <>
                  <ToolTipComponent content={t("Expand All")}>
                    <ButtonComponent
                      disabled={conditions.length === 0}
                      iconName="ExpandAllGroup"
                      iconButton
                      onClick={() => toggleGrouping?.("expand")}
                    />
                  </ToolTipComponent>
                  <ToolTipComponent content={t("Collapse All")}>
                    <ButtonComponent
                      disabled={conditions.length === 0}
                      iconName="CollapseAllGroup"
                      iconButton
                      onClick={() => toggleGrouping?.("collapse")}
                    />
                  </ToolTipComponent>
                </>
              )}
              <ButtonComponent
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                disabled={!isAllConditionsValid() || !!tempCondition}
                loading={false}
                onClick={handleAdd}
                startIcon={<AddIcon />}
              >
                <Text className="inline">{t("Add")}</Text>
              </ButtonComponent>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto pt-4">
            {list.length > 0 || tempCondition ? (
              list.map((c, idx) => {
                const usedByOthers = new Set(
                  list
                    .filter((_, i) => i !== idx)
                    .map((x) => x.column)
                    .filter(Boolean),
                );
                return (
                  <ConditionRow
                    key={c.id ?? idx}
                    item={c}
                    isFirst={idx === 0}
                    title={title}
                    columnDefinitions={columnDefinitions}
                    usedColumns={usedByOthers}
                    onChangeColumn={(val) => {
                      const arr: Condition[] = [...list];
                      const current = arr[idx];
                      if (!current) return;
                      arr[idx] = { ...current, column: val };
                      setConditions(arr);
                    }}
                    onToggleDirection={() => toggleDirection(idx)}
                    onDelete={() => handleDelete(idx)}
                  />
                );
              })
            ) : (
              <Text className="text-[#666A84]">
                {t("No {{type}} conditions are applied.", { type: title.toLowerCase() })}
              </Text>
            )}

            {tempCondition && (
              <div className="flex items-center gap-2 px-2 py-2">
                <span className="cursor-grab active:cursor-grabbing">
                  <IconComponent name="Drag" width="1rem" height="1rem" />
                </span>

                <span className="w-20 text-sm font-medium">{headerText}</span>

                <AutoComplete
                  disableClearable
                  PopperComponent={ModalAutoCompletePopper}
                  options={columnDefinitions.filter((col) => {
                    if (["_checkbox_selector", "_move"].includes(col.id ?? "")) {
                      return false;
                    }
                    if (allUsedColumns.has(col.field)) return false;
                    if (title === "Sort") {
                      return !col.disableSorting;
                    } else {
                      return !col.disableGrouping;
                    }
                  })}
                  value={columnDefinitions.find((col) => col.field === tempCondition.column) ?? undefined}
                  onChange={(_e: React.SyntheticEvent, val: ColumnDefinition | null) => {
                    if (val?.field) {
                      handleTempChange("column", val.field);
                    }
                  }}
                  getOptionLabel={(v: ColumnDefinition) => (v?.name as string) || ""}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextFieldComponent
                      {...params}
                      size="small"
                      placeholder={t("Select column")}
                      className="px-2 py-1 flex-1 min-w-0"
                    />
                  )}
                  sx={{ flex: 1, minWidth: "11.25rem" }}
                />

                <ButtonComponent
                  buttonType={BUTTON_TYPES.TEXT_BUTTON}
                  iconButton={true}
                  onClick={() => setTempCondition(null)}
                >
                  <IconComponent name="delete" width="1.25rem" height="1.25rem" />
                </ButtonComponent>
              </div>
            )}
          </div>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}
